import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, Plus, Minus, CreditCard, 
  Banknote, Phone, Loader2, CheckCircle2, XCircle, ShoppingCart, RefreshCw
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import {
  fetchCart, updateCartItemQuantity, checkoutCart, checkPaymentStatus, retryPayment, Cart, CheckoutRequest, scanCartItem
} from '@/api/cart';

const calculateBackoffDelay = (attempt: number): number => {
  const baseDelay = 1000;
  const maxDelay = 10000;
  return Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
};

const CartPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toastCtx = useToast();

  const showToast = (opts: { type: 'success' | 'error' | 'warning' | 'info'; message: string }) => {
    if (opts.type === 'success') toastCtx.success(opts.message);
    else if (opts.type === 'error') toastCtx.error('Error', opts.message);
    else if (opts.type === 'warning') toastCtx.warning('Warning', opts.message);
    else if (opts.type === 'info') toastCtx.info('Info', opts.message);
  };

  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);

  // Checkout Modal State
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'mpesa'>('cash');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState('0');
  const [mpesaManualCode, setMpesaManualCode] = useState('');
  const [processing, setProcessing] = useState(false);

  // Payment Status Polling State
  const [paymentStatusModalVisible, setPaymentStatusModalVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'checking' | 'success' | 'failed'>('checking');
  const [currentSaleId, setCurrentSaleId] = useState<string | null>(null);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [preCheckoutCart, setPreCheckoutCart] = useState<Cart | null>(null);

  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkCountRef = useRef(0);

  // Hardware Scanner State
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeystrokeTime, setLastKeystrokeTime] = useState(0);

  useEffect(() => {
    loadCart();
    return () => stopPaymentStatusPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Hardware Scanner Listener Effect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        return;
      }

      const currentTime = new Date().getTime();

      if (e.key === 'Enter') {
        if (barcodeBuffer.length > 3) {
          processBarcode(barcodeBuffer);
        }
        setBarcodeBuffer('');
      } else if (e.key.length === 1) {
        if (currentTime - lastKeystrokeTime > 100) {
          setBarcodeBuffer(e.key);
        } else {
          setBarcodeBuffer(prev => prev + e.key);
        }
        setLastKeystrokeTime(currentTime);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [barcodeBuffer, lastKeystrokeTime, businessId]);

  const processBarcode = async (scannedCode: string) => {
    if (!businessId) return;
    try {
      showToast({ type: 'info', message: `Scanning ${scannedCode}...` });
      const res = await scanCartItem(businessId, scannedCode);
      if (res.success) {
        showToast({ type: 'success', message: `Added to cart` });
        loadCart(); // refresh cart
      }
    } catch (err: any) {
      console.error('Scan failed:', err);
      showToast({ type: 'error', message: err.response?.data?.error || 'Item not found' });
    }
  };

  const loadCart = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const data = await fetchCart(businessId);
      setCart(data);
    } catch (err) {
      console.error('Failed to load cart', err);
      showToast({ type: 'error', message: 'Failed to load cart' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (itemId: string, newQuantity: number) => {
    if (!businessId) return;
    if (newQuantity < 0) return;
    try {
      await updateCartItemQuantity(businessId, itemId, newQuantity);
      loadCart();
    } catch (err: any) {
      showToast({ type: 'error', message: err.response?.data?.error || 'Failed to update quantity' });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    handleUpdateQuantity(itemId, 0);
  };

  const resetCheckoutForm = () => {
    setPaymentMethod('cash');
    setCustomerPhone('');
    setDiscount('0');
    setMpesaManualCode('');
  };

  const handleCheckoutSubmit = async () => {
    if (!businessId || processing) return;

    if (!cart?.cart_items?.length) {
      showToast({ type: 'warning', message: 'Cart is empty' });
      return;
    }

    const hasManualCode = mpesaManualCode.trim().length > 0;
    if (paymentMethod === 'mpesa' && !hasManualCode && !customerPhone) {
      showToast({ type: 'warning', message: 'Phone required for M-Pesa' });
      return;
    }

    const sanitizedPhone = customerPhone.replace(/\D/g, '');
    if (paymentMethod === 'mpesa' && !hasManualCode && sanitizedPhone.length < 9) {
      showToast({ type: 'warning', message: 'Valid phone required' });
      return;
    }

    setProcessing(true);
    setPreCheckoutCart(cart);

    try {
      const request: CheckoutRequest = {
        payment_method: paymentMethod,
        amount: cart?.total_amount || 0,
        customer_phone: sanitizedPhone,
        discount: parseFloat(discount) || 0,
        mpesa_code: hasManualCode ? mpesaManualCode.trim() : undefined,
      };

      const res = await checkoutCart(businessId, request);

      if (paymentMethod === 'mpesa' && res.mpesa) {
        setCurrentSaleId(res.sale.id);
        setPaymentStatus('checking');
        setPaymentStatusModalVisible(true);
        setCheckoutModalVisible(false);
        checkCountRef.current = 0;
        startPaymentStatusPolling(res.sale.id);
      } else {
        showSuccessAlert(res.receipt);
        setPreCheckoutCart(null);
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const msg = error.response?.data?.error || error.message || 'Checkout failed';
      showToast({ type: 'error', message: msg });
    } finally {
      setProcessing(false);
    }
  };

  const startPaymentStatusPolling = (saleId: string) => {
    stopPaymentStatusPolling();
    checkMpesaPaymentStatus(saleId);
  };

  const stopPaymentStatusPolling = () => {
    if (pollingIntervalRef.current) clearTimeout(pollingIntervalRef.current);
    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    checkCountRef.current = 0;
  };

  const checkMpesaPaymentStatus = async (saleId: string) => {
    if (!businessId) return;
    try {
      checkCountRef.current += 1;
      const maxAttempts = 30; // ~2 mins

      if (checkCountRef.current > maxAttempts) {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage('Payment timeout. Customer may not have completed the payment.');
        await restoreCartAfterFailure();
        return;
      }

      const res = await checkPaymentStatus(businessId, saleId);

      if (res.status === 'completed') {
        stopPaymentStatusPolling();
        setPaymentStatus('success');
        pollingTimeoutRef.current = setTimeout(() => {
          setPaymentStatusModalVisible(false);
          loadCart();
          resetCheckoutForm();
          setPreCheckoutCart(null);
          showToast({ type: 'success', message: 'Sale Completed!' });
        }, 2000);
      } else if (res.status === 'failed') {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage(res.message || 'Payment failed');
        await restoreCartAfterFailure();
      } else {
        const delay = calculateBackoffDelay(checkCountRef.current);
        pollingIntervalRef.current = setTimeout(() => checkMpesaPaymentStatus(saleId), delay);
      }
    } catch (error) {
      if (checkCountRef.current < 30) {
        const delay = calculateBackoffDelay(checkCountRef.current);
        pollingIntervalRef.current = setTimeout(() => checkMpesaPaymentStatus(saleId), delay);
      } else {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage('Unable to verify payment status. Check manually.');
        await restoreCartAfterFailure();
      }
    }
  };

  const restoreCartAfterFailure = async () => {
    if (!preCheckoutCart?.cart_items?.length || !businessId) return;
    try {
      // Basic reload to see if items stayed in DB
      await loadCart();
    } catch (error) {
      console.error('Failed to restore cart', error);
    }
  };

  const handleRetryPayment = async () => {
    if (!businessId || !currentSaleId) return;
    try {
      await retryPayment(businessId, currentSaleId);
      setPaymentStatus('checking');
      checkCountRef.current = 0;
      startPaymentStatusPolling(currentSaleId);
      showToast({ type: 'info', message: 'Payment retry initiated' });
    } catch (error: any) {
      showToast({ type: 'error', message: error.response?.data?.error || 'Retry failed' });
    }
  };

  const showSuccessAlert = (receipt: any) => {
    const total = receipt?.total || receipt?.amount || 0;
    showToast({ type: 'success', message: `Sale Completed! Receipt: ${receipt?.receipt_number || 'PENDING'}, Total: KES ${total}` });
    setCheckoutModalVisible(false);
    loadCart();
    resetCheckoutForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-[#121212]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const subtotal = cart?.total_amount || 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212] pb-24 lg:pb-0">
      {/* Header */}
      <div className="sticky top-0 z-30 h-16 bg-white dark:bg-[#1E1E1E] shadow-sm flex items-center px-4 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-[#2A2A2A] hover:opacity-80 transition mr-4"
        >
          <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-200" />
        </button>
        <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          POS Cart
        </h1>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {!cart?.cart_items?.length ? (
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-12 flex flex-col items-center justify-center text-center shadow-sm">
                <ShoppingCart className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Cart is empty</h3>
                <p className="text-gray-500 dark:text-gray-400">Add products from your inventory to start a sale.</p>
                <button 
                  onClick={() => navigate(`/business/${businessId}/barcodes`)}
                  className="mt-6 px-6 py-2 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition"
                >
                  Go to Inventory
                </button>
              </div>
            ) : (
              cart.cart_items.map((item) => (
                <div key={item.id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-4 flex items-center gap-4 shadow-sm">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-[#2A2A2A] flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {item.product.image_url ? (
                      <img src={item.product.image_url} alt={item.product.name} className="w-full h-full object-cover" />
                    ) : (
                      <ShoppingCart className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 dark:text-white line-clamp-1">{item.product.name}</h4>
                    <p className="text-sm font-semibold text-primary mt-1">KES {item.product.price.toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-lg p-1">
                      <button 
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-md bg-white dark:bg-[#1E1E1E] shadow-sm text-gray-600 dark:text-gray-300 hover:text-danger"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="font-bold text-gray-900 dark:text-white w-6 text-center">{item.quantity}</span>
                      <button 
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-md bg-white dark:bg-[#1E1E1E] shadow-sm text-gray-600 dark:text-gray-300 hover:text-primary"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <button 
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-sm text-danger hover:underline font-semibold flex items-center gap-1"
                    >
                      <Trash2 className="h-4 w-4" /> Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 shadow-sm sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Order Summary</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900 dark:text-white">KES {subtotal.toLocaleString()}</span>
                </div>
                {/* Discount and Taxes can be added here if needed */}
                <div className="pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-between">
                  <span className="font-bold text-gray-900 dark:text-white">Total</span>
                  <span className="font-black text-primary text-xl">KES {subtotal.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={() => setCheckoutModalVisible(true)}
                disabled={!cart?.cart_items?.length}
                className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 disabled:opacity-50 transition shadow-lg shadow-primary/30"
              >
                Checkout KES {subtotal.toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {checkoutModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1E1E1E] w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setCheckoutModalVisible(false)}
              className="absolute top-4 right-4 p-2 bg-gray-100 dark:bg-[#2A2A2A] rounded-full hover:opacity-80 text-gray-500 dark:text-gray-400"
            >
              <XCircle className="h-6 w-6" />
            </button>
            
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 text-center">Complete Sale</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`py-4 rounded-xl font-bold flex flex-col items-center gap-2 border-2 transition ${
                  paymentMethod === 'cash' 
                    ? 'border-primary bg-primary/10 text-primary' 
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#2A2A2A] text-gray-500 dark:text-gray-400'
                }`}
              >
                <Banknote className="h-6 w-6" /> Cash
              </button>
              <button
                onClick={() => setPaymentMethod('mpesa')}
                className={`py-4 rounded-xl font-bold flex flex-col items-center gap-2 border-2 transition ${
                  paymentMethod === 'mpesa' 
                    ? 'border-green-500 bg-green-500/10 text-green-600' 
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#2A2A2A] text-gray-500 dark:text-gray-400'
                }`}
              >
                <Phone className="h-6 w-6" /> M-Pesa
              </button>
            </div>

            {paymentMethod === 'mpesa' && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Customer Phone</label>
                  <input
                    type="tel"
                    placeholder="e.g. 0712345678"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave blank if customer already paid manually</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Manual M-Pesa Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. QWE123RTY"
                    value={mpesaManualCode}
                    onChange={(e) => setMpesaManualCode(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary uppercase"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Discount Amount (KES)</label>
              <input
                type="number"
                placeholder="0"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary mb-6"
              />
            </div>

            <button
              onClick={handleCheckoutSubmit}
              disabled={processing}
              className={`w-full py-4 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 ${
                paymentMethod === 'mpesa' ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30'
              } disabled:opacity-50`}
            >
              {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
              {paymentMethod === 'mpesa' ? 'Send STK Push' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      )}

      {/* Payment Status Modal */}
      {paymentStatusModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white dark:bg-[#1E1E1E] w-full max-w-sm rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center">
            
            {paymentStatus === 'checking' && (
              <>
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center relative z-10">
                    <Loader2 className="h-10 w-10 text-green-500 animate-spin" />
                  </div>
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Awaiting Payment</h3>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  Please ask the customer to enter their M-Pesa PIN on their phone.
                </p>
              </>
            )}

            {paymentStatus === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Payment Successful!</h3>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  The sale has been recorded successfully.
                </p>
              </>
            )}

            {paymentStatus === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
                  <XCircle className="h-10 w-10 text-red-500" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Payment Failed</h3>
                <p className="text-sm font-semibold text-red-500 mb-6 px-4">
                  {paymentErrorMessage}
                </p>
                <div className="flex gap-4 w-full">
                  <button
                    onClick={() => setPaymentStatusModalVisible(false)}
                    className="flex-1 py-3 bg-gray-100 dark:bg-[#2A2A2A] text-gray-900 dark:text-white font-bold rounded-xl hover:opacity-80 transition"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleRetryPayment}
                    className="flex-1 py-3 bg-primary text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition"
                  >
                    <RefreshCw className="h-4 w-4" /> Retry
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default CartPage;
